import { collection, doc, onSnapshot, runTransaction, Timestamp } from "firebase/firestore";
import { auth, dbAccounts } from "../../../../backend/firebaseConfig";
import { ACTION_STATUSES } from "../constants/guidelineCharges";

const reviews = collection(dbAccounts, "MonthlyChargeReviews");
export const chargeSourceKey = (row) => row.id.startsWith("ifa-rate-") ? row.id.replace(/-\d{4}-\d{2}$/, "") : row.id;
export const reviewKey = (row, month) => encodeURIComponent(`${chargeSourceKey(row)}|${month}`);
export const hasMissingAdviserEmail = (row) =>
  row.adviserEmailUnlinked === true ||
  !String(row.adviserEmail || "").trim();
export const hasChargeVariance = (row) =>
  Number.isFinite(row.amountCharged) && Number.isFinite(row.expectedAmount) &&
  (row.forceFlag === true || Math.round(row.amountCharged * 100) !== Math.round(row.expectedAmount * 100));
export const requiresChargeReview = (row) =>
  hasMissingAdviserEmail(row) || hasChargeVariance(row);
export const chargeSignature = (row) => JSON.stringify([row.amountCharged, row.expectedAmount, !!row.forceFlag, !!row.adviserEmailUnlinked, row.description, row.adviserEmail || ""]);
export const subscribeChargeReviews = (onData, onError) => onSnapshot(reviews,
  (snapshot) => onData(snapshot.docs.map((entry) => ({ ...entry.data(), reviewId: entry.id }))), onError);

const snapshotOf = (row) => Object.fromEntries([
  "id", "adviser", "adviserEmail", "client", "chargeType", "description", "amountLabel",
  "amountCharged", "expectedAmount", "expectedGuideline", "sourcePath", "forceFlag", "flagReason",
  "adviserEmailUnlinked", "billingMonth",
].filter((key) => row[key] !== undefined).map((key) => [key, row[key]]));

export const saveChargeReview = async (row, month, status, { automatic = false, expectedStatus, signature } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Sign in to update a charge status.");
  if (!ACTION_STATUSES.some((item) => item.id === status)) throw new Error("Unknown charge status.");
  if (status === "corrected-at-source" && hasChargeVariance(row)) {
    throw new Error("Correct the source charge first. It still requires review.");
  }
  const reference = doc(reviews, reviewKey(row, month));
  await runTransaction(dbAccounts, async (transaction) => {
    const existing = await transaction.get(reference);
    const previous = existing.exists() ? existing.data() : null;
    if (automatic && expectedStatus === null && previous) return;
    if (automatic && expectedStatus && previous?.actionStatus !== expectedStatus) return;
    if (previous?.actionStatus === status && previous?.signature === chargeSignature(row)) return;
    const changedAt = Timestamp.now();
    const event = { status, previousStatus: previous?.actionStatus || null, changedAt,
      changedBy: user.email || user.uid, changedByName: user.displayName || user.email || user.uid,
      automatic, amountCharged: row.amountCharged, expectedAmount: row.expectedAmount };
    transaction.set(reference, {
      ...snapshotOf(row), sourceKey: chargeSourceKey(row), billingMonth: month,
      date: row.date?.toDate?.()?.toISOString() || String(row.date || `${month}-01`),
      actionStatus: status, signature: signature || chargeSignature(row),
      history: [...(previous?.history || []), event], ...event,
    });
  });
};
